import { Button, Grid, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { Field, Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { TextField } from 'formik-mui';
import { sortBy } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { cascadesApi } from 'api';
import {
  DataWrapper,
  FormActions,
  FormControls,
  FormikSelect,
  PageHeader,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey, RequisitesCascadeMode } from 'enums';
import { useMutation, useAppHistory } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Cascade } from 'types';
import { validationUtils } from 'utils';

import { CascadeP2PProviders } from './CascadeP2PProviders';

export type Values = Pick<
  Cascade,
  'name' | 'payinCascadeMode' | 'payoutCascadeMode' | 'p2pProvidersConfigs'
>;

type CreateCascadePayload = Omit<Values, 'p2pProvidersConfigs'> & {
  p2pProvidersConfigs: Array<{
    p2pProviderId: string;
    priority: number;
  }>;
};

export const ManageCascade: React.FC = () => {
  const { id: cascadeId = '' } = useParams<{ id: string }>();
  const { goBack } = useAppHistory();

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades.manage',
  });
  const { t: tCommon } = useTranslation();

  const isNew = useMemo(() => cascadeId === NEW_ID, [cascadeId]);

  const title = useMemo(
    () => t(isNew ? 'new_cascade.title' : 'edit_cascade.title'),
    [isNew, t],
  );

  const backUrl = useMemo(() => ROUTE_PATH.ADMIN.CASCADES, []);

  const initialState: Values = useMemo(
    () => ({
      name: '',
      payinCascadeMode: RequisitesCascadeMode.TradersFirst,
      payoutCascadeMode: RequisitesCascadeMode.TradersFirst,
      p2pProvidersConfigs: [],
    }),
    [],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(tCommon('errors.required')),
        payinCascadeMode: Yup.string()
          .oneOf(Object.values(RequisitesCascadeMode))
          .required(tCommon('errors.required')),
        payoutCascadeMode: Yup.string()
          .oneOf(Object.values(RequisitesCascadeMode))
          .required(tCommon('errors.required')),
        p2pProvidersConfigs: Yup.array()
          .of(
            Yup.object().shape({
              p2pProviderId: Yup.string().required(tCommon('errors.required')),
            }),
          )
          .min(1, tCommon('errors.at_least_one_provider_required')),
      }),
    [tCommon],
  );

  const [initialValues, setInitialValues] = useState<Values>(initialState);

  const queryClient = useQueryClient();
  const queryResult = useQuery(
    [QueryKey.Cascades, cascadeId],
    () => cascadesApi.getOne(cascadeId),
    {
      enabled: !!cascadeId && !isNew,
      onSuccess: (data) => {
        setInitialValues({
          name: data.name,
          payinCascadeMode: data.payinCascadeMode,
          payoutCascadeMode: data.payoutCascadeMode,
          p2pProvidersConfigs: sortBy(
            data.p2pProvidersConfigs || [],
            'priority',
          ),
        });
      },
      keepPreviousData: true,
    },
  );

  const { mutate: createCascade } = useMutation<
    Cascade,
    AxiosError,
    Partial<Cascade>
  >(cascadesApi.create);
  const { mutate: updateCascade } = useMutation<
    Cascade,
    AxiosError,
    { id: string; data: Partial<Cascade> }
  >(({ id, data }) => cascadesApi.update({ id, data }));

  const handleSubmit = useCallback(
    (cascade: Values, formikHelpers: FormikHelpers<Values>) => {
      const options = {
        onSuccess: () => {
          queryClient.invalidateQueries(QueryKey.Cascades);
          goBack(backUrl);
        },
        onError: (error: AxiosError) =>
          formikHelpers.setErrors(validationUtils.getFormErrors(error)),
        onSettled: () => formikHelpers.setSubmitting(false),
      };

      formikHelpers.setSubmitting(true);

      const udpatedCascade: CreateCascadePayload = {
        ...cascade,
        p2pProvidersConfigs: (cascade.p2pProvidersConfigs || []).map(
          (config, index) => ({
            p2pProviderId: config.p2pProviderId,
            priority: index,
          }),
        ),
      };

      if (isNew) {
        createCascade(udpatedCascade as Partial<Cascade>, options);
      } else {
        updateCascade(
          { id: cascadeId, data: udpatedCascade as Partial<Cascade> },
          options,
        );
      }
    },
    [
      isNew,
      queryClient,
      goBack,
      backUrl,
      createCascade,
      updateCascade,
      cascadeId,
    ],
  );

  const handleCancel = useCallback(() => goBack(backUrl), [goBack, backUrl]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: handleSubmit,
  });

  const payinModeOptions = useMemo(
    () => [
      {
        value: RequisitesCascadeMode.TradersFirst,
        label: t('modes.traders_first'),
      },
      {
        value: RequisitesCascadeMode.ProvidersFirst,
        label: t('modes.providers_first'),
      },
      {
        value: RequisitesCascadeMode.TradersOnly,
        label: t('modes.traders_only'),
      },
      {
        value: RequisitesCascadeMode.ProvidersOnly,
        label: t('modes.providers_only'),
      },
      { value: RequisitesCascadeMode.Parallel, label: t('modes.parallel') },
    ],
    [t],
  );

  const payoutModeOptions = useMemo(
    () => [
      {
        value: RequisitesCascadeMode.TradersFirst,
        label: t('modes.default'), // renamed to avoid confusion
      },
      {
        value: RequisitesCascadeMode.TradersOnly,
        label: t('modes.traders_only'),
      },
      {
        value: RequisitesCascadeMode.ProvidersOnly,
        label: t('modes.providers_only'),
      },
    ],
    [t],
  );

  return (
    <Fragment>
      <PageHeader title={title} />
      <FormikProvider value={formik}>
        <DataWrapper queryResult={!isNew ? queryResult : undefined}>
          <div className="tw-max-w-lg">
            <Form>
              <Grid container spacing={6}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControls>
                    <Typography variant="subtitle1">
                      {t('sections.basic')}
                    </Typography>

                    <Field
                      component={TextField}
                      label={t('fields.name')}
                      name="name"
                      size="small"
                      required
                      fullWidth
                    />

                    <FormikSelect
                      label={t('fields.payin_cascade_mode')}
                      name="payinCascadeMode"
                      options={payinModeOptions}
                      required
                    />

                    <FormikSelect
                      label={t('fields.payout_cascade_mode')}
                      name="payoutCascadeMode"
                      options={payoutModeOptions}
                      required
                    />
                  </FormControls>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControls>
                    <CascadeP2PProviders />
                  </FormControls>
                </Grid>
              </Grid>

              <FormActions>
                <Button variant="outlined" onClick={handleCancel}>
                  {tCommon('buttons.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting || !formik.isValid}
                  onClick={formik.submitForm}
                >
                  {tCommon('buttons.save')}
                </Button>
              </FormActions>
            </Form>
          </div>
        </DataWrapper>
      </FormikProvider>
    </Fragment>
  );
};
